'use client'

import React, {  } from 'react'
import { ChildProp } from 'types/common'
import { useAppDispatch, useAppSelector } from 'features/hooks'
import { RootState } from 'features/store'
import { useRouter } from 'next/navigation'
import { X } from 'lucide-react'
import {
  Dialog,
  Card,
  CardBody,
  IconButton,
  Typography,
} from '@material-tailwind/react'
import { toast } from 'react-toastify'
import { Formik } from 'formik'
import { ChangePasswordSchema } from 'app/admin/utils/authValidation'
import {
  useResetPasswordMutation,
  useUserProfileQuery,
} from 'graphql/generated/graphql'
import PublicWrapper from '@/lib/publicWrapper'
import SwitchUserRole from '@/components/profile/SwitchUserRole'
import { setProfileSettingsModal } from 'features/generalSlice'
import { useSelector } from 'react-redux'
import ProfileSettings from '@/components/profile/ProfileSettings'
import { Dialog as CustomDialog, DialogContent } from '@/components/ui/dialog'
import Header from '@/components/Header'
import { logOut, logOutAndClearReminder } from 'features/authSlice'
import { SidebarCulineer } from '@/components/Sidebar'
import MobileNavigation from '@/components/MobileNavigation'
import { isUserAvailable } from 'helpers/authUser'
import ExternalWebsiteSidebar from '@/components/ExternalWebsiteSidebar'
import { Navbar } from '@/components/navbar'
import { Footer } from '@/components/footer'
import { isTokenExpired } from '@/lib/utils'

// Read auth token synchronously from redux-persist localStorage before Redux hydrates.
// Prevents authenticated users from seeing the marketing layout flash on first render.
function hasSyncToken(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const raw = localStorage.getItem('persist:root')
    if (!raw) return false
    const root = JSON.parse(raw)
    const auth = JSON.parse(root.culineerAuth || '{}')
    return !!auth.jwt_token && !isTokenExpired(auth.jwt_token)
  } catch {
    return false
  }
}

const PublicLayout: React.FC<ChildProp> = ({ children }) => {
  const [toggled, setToggled] = React.useState(false)
  const [collapsed, setCollapsed] = React.useState(false)
  const [changePasswordModal, setChangePasswordModal] =
    React.useState<boolean>(false)
  const [switchRoleModal, setSwitchRoleModal] = React.useState<boolean>(false)
  const [externalSidebarOpen, setExternalSidebarOpen] = React.useState<boolean>(false)

  const { user } = useAppSelector((state: RootState) => state.culineerAuth)
  const profileSettingsModal = useSelector(
    (state: RootState) => state.general.openProfileSettingsModal
  )

  const handleExternalSidebarToggle = () => setExternalSidebarOpen((cur) => !cur)

  const handleProfileSettingsOpen = () =>
    dispatch(setProfileSettingsModal(!profileSettingsModal))

  const { data: userDetail } = useUserProfileQuery({
    fetchPolicy: 'no-cache',
    variables: {
      slug: user?.slug,
    },
    skip: !user?.slug,
  })

  const [resetPassword] = useResetPasswordMutation({ fetchPolicy: 'no-cache' })
  const dispatch = useAppDispatch()

  const router = useRouter()

  const handlePasswordOpen = () => setChangePasswordModal((cur) => !cur)
  const handleSwitchOpen = () => setSwitchRoleModal((cur) => !cur)

  // Defer localStorage token read until after mount so SSR and the first client
  // paint render the same tree (avoids Navbar vs app-shell hydration mismatch).
  const [mounted, setMounted] = React.useState(false)
  React.useEffect(() => {
    setMounted(true)
  }, [])

  // Redux-persist rehydrates before mount — only decide layout after mount so SSR
  // and the first client paint render the same tree.
  const userAvailable =
    mounted && (isUserAvailable(user) || hasSyncToken())

  // Non-auth visitors (shared links) get the marketing layout — full Navbar,
  // Footer, and exploration links rather than the locked app shell.
  if (!userAvailable) {
    return (
      <PublicWrapper>
        <>
          <Navbar />
          <main className="min-h-screen">
            {children}
          </main>
          <Footer />
        </>
      </PublicWrapper>
    )
  }

  return (
    <PublicWrapper>
      <div className="flex h-screen w-full flex-col bg-white">
        <Header
          setToggled={setToggled}
          toggled={toggled}
          handlePasswordOpen={handlePasswordOpen}
          handleSwitchOpen={handleSwitchOpen}
          handleProfileSettingsOpen={handleProfileSettingsOpen}
          handleExternalSidebarToggle={handleExternalSidebarToggle}
          userDetail={userDetail}
        />

        {/* Main Content */}
        <div className="flex w-full overflow-hidden sm:h-[calc(100vh-70px)] xs:h-[93%]">
          {/* Sidebar */}
          <SidebarCulineer
            setToggled={setToggled}
            toggled={toggled}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            userDetail={userDetail}
          />

          {/* Main Content Area */}
          <main
            className={`flex h-full w-full justify-center overflow-hidden overflow-y-auto border-none pt-4 xxxs:w-full xxs:w-full xs:w-full sm:pb-6 pb-[calc(5rem+env(safe-area-inset-bottom))]`}
          >
            {children}
          </main>
        </div>

        <MobileNavigation isAuthenticated={isUserAvailable(user)} />

        {/* Modals */}
        <Dialog
          open={changePasswordModal}
          handler={handlePasswordOpen}
          data-modal-backdrop="static"
          data-dialog-close={false}
          data-dialog-backdrop-close={false}
          className="bg-transparent shadow-none"
        >
          <Card className="mx-auto w-full max-w-[24rem]">
            <CardBody className="flex flex-col gap-4">
              <div className="flex justify-between">
                <Typography variant="h6" color="blue-gray">
                  Change Password
                </Typography>
                <IconButton
                  color="blue-gray"
                  size="sm"
                  variant="text"
                  onClick={handlePasswordOpen}
                >
                  <X className="h-5 w-5" />
                </IconButton>
              </div>
              <Formik
                initialValues={{
                  oldPassword: '',
                  newPassword: '',
                  confirmPassword: '',
                }}
                validationSchema={ChangePasswordSchema}
                onSubmit={async (values, { setSubmitting }) => {
                  try {
                    const response = await resetPassword({
                      variables: {
                        input: {
                          email_address: user?.email_address,
                          currentPassword: values.oldPassword,
                          newPassword: values.newPassword,
                        },
                      },
                    })
                    if (response !== undefined) {
                      setSubmitting(false)
                      toast.success(
                        'Password changed successfully! Log in to continue.'
                      )
                      dispatch(logOutAndClearReminder())
                      return router.push('/auth/login')
                    }
                  } catch (error: any) {
                    setSubmitting(false)
                    return toast.error(error.message)
                  }
                }}
              >
                {/* ... Formik render props ... */}
              </Formik>
            </CardBody>
          </Card>
        </Dialog>

        <CustomDialog
          open={profileSettingsModal}
          onOpenChange={handleProfileSettingsOpen}
        >
          <DialogContent className="max-h-[90vh] w-full max-w-[626px] overflow-y-auto overflow-x-hidden bg-white px-4 py-4 font-inter text-black">
            <div className="flex w-full items-center justify-between pb-3 pl-3">
              <Typography variant="h5" color="blue-gray">
                Edit Account
              </Typography>
            </div>
            <ProfileSettings
              handleProfileSettingsOpen={handleProfileSettingsOpen}
            />
          </DialogContent>
        </CustomDialog>

        {user?.userRoles?.length > 1 && (
          <SwitchUserRole
            userId={user?.user_id}
            roles={user?.userRoles}
            handleSwitchOpen={handleSwitchOpen}
            switchRoleModal={switchRoleModal}
            setSwitchRoleModal={setSwitchRoleModal}
          />
        )}
        <ExternalWebsiteSidebar
          isOpen={externalSidebarOpen}
          onClose={() => setExternalSidebarOpen(false)}
          url="https://scribehow.com/page/Step_by_Step_Guidance__rKRAE8ihS6qBbayMQLRLPg?referrer=workspace"
        />
      </div>
    </PublicWrapper>
  )
}

export default PublicLayout
